import { useEffect, useState } from 'react';
import { api, type CardKind, type CardUploadPreview } from '../lib/api';
import { DataTable, ErrorBox, money, type Column, type Row } from '../components/DataTable';

/**
 * 재무추가 **카드 대량등록** — 레거시 `CreateMethod='카드대량등록'`(25,882행) 이식.
 *
 * 엑셀 대량등록과 갈라지는 지점은 **매칭 키 하나**다.
 *   엑셀 → 편성번호(PK, 모호성 없음)
 *   카드 → **카드번호** → 편성의 `주유카드번호`/`하이패스카드번호`
 *
 * 그래서 화면도 다르다: 사용자는 편성번호를 모르고 **카드사 명세서**를 들고 온다.
 *
 * ⚠️ 카드사 원본 파일 형식(하나/우리/국민)은 제각각이고 우리에겐 실물 샘플이 없다.
 *    그래서 **우리 열 이름으로 정규화한 시트**를 받는다 — 명세서에서 3열만 복사해 붙이면 된다.
 *    (엑셀 대량등록 때 템플릿을 새로 만든 것과 같은 판단이다.)
 *
 * ⚠️ **빈 카드번호는 거부된다.** 편성 3,847건 중 카드번호가 빈 것이 3,576건이라,
 *    빈 키를 통과시키면 한 행이 3,576건과 매칭된다.
 */

const KINDS: CardKind[] = ['유류', '하이패스'];

interface ParsedRow { cardNo: string; amount: string; subsidy: string }

export default function ResultDailyCardUpload({ driveMon, onDone }: { driveMon: string; onDone: () => void }) {
  const [kind, setKind] = useState<CardKind>('유류');
  const [corps, setCorps] = useState<string[]>([]);
  const [corp, setCorp] = useState('');
  const [prefix, setPrefix] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<CardUploadPreview | null>(null);
  const [busy, setBusy] = useState<'template' | 'parse' | 'check' | 'commit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ created: number; plus: string; minus: string } | null>(null);

  const columns = kind === '유류' ? ['카드번호', '사용금액', '보조금'] : ['카드번호', '사용금액'];

  // 기본 비고 앞머리 — 레거시 최빈 문구 형태(`2026.06 하나카드`). 사용자가 고칠 수 있다.
  useEffect(() => {
    setPrefix(`${driveMon.replace('-', '.')}${corp ? ` ${corp}` : ''}`);
  }, [driveMon, corp]);

  // 카드사 목록은 그 달·그 종류의 편성에서 나온다 — 종류를 바꾸면 목록도 바뀐다.
  useEffect(() => {
    let alive = true;
    setCorp(''); setPreview(null); setDone(null);
    api.getCardUploadTemplate({ driveMon, cardKind: kind })
      .then((t) => { if (alive) setCorps(t.corps); })
      .catch((err) => { if (alive) setError(err instanceof Error ? err.message : String(err)); });
    return () => { alive = false; };
  }, [driveMon, kind]);

  /** 템플릿 — 입력 시트 + **카드번호 목록** 시트. 후자가 실질적인 도움이다(매칭 실패를 미리 잡는다). */
  async function downloadTemplate() {
    setBusy('template'); setError(null);
    try {
      const [XLSX, t] = await Promise.all([
        import('xlsx'),
        api.getCardUploadTemplate({ driveMon, cardKind: kind }),
      ]);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        [...columns],
        [t.allocs[0]?.card_num ?? '', 0, ...(kind === '유류' ? [0] : [])],
      ]), '입력');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['카드번호', '카드사', '편성번호', '소속', '차주', '기사', '차량번호'],
        ...t.allocs.map((a) => [
          a.card_num ?? '', a.card_corp ?? '', a.car_alloc_id,
          a.grp_name ?? '', a.owner_name ?? '', a.driver_name ?? '', a.car_no ?? '',
        ]),
      ]), '카드번호목록');

      XLSX.writeFile(wb, `재무추가_카드_${kind}_${driveMon}_템플릿.xlsx`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('parse'); setError(null); setPreview(null); setDone(null);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]!];
      if (!sheet) throw new Error('시트를 찾을 수 없습니다.');
      // 카드번호는 반드시 문자열로 받는다 — 숫자로 받으면 앞자리 0 과 하이픈이 날아간다.
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { raw: false, defval: '' });
      if (raw.length === 0) throw new Error('행이 없습니다.');

      const head = Object.keys(raw[0]!);
      const missing = ['카드번호', '사용금액'].filter((c) => !head.includes(c));
      if (missing.length) {
        throw new Error(`엑셀에 다음 열이 없습니다: ${missing.join(', ')} (필요한 열: ${columns.join(' · ')})`);
      }

      setRows(raw.map((r) => ({
        cardNo: String(r['카드번호'] ?? '').trim(),
        amount: String(r['사용금액'] ?? '').trim(),
        subsidy: String(r['보조금'] ?? '').trim(),
      })));
      setFileName(file.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows([]); setFileName('');
    } finally {
      setBusy(null);
      e.target.value = '';
    }
  }

  const payload = () => ({
    driveMon, cardKind: kind, cardCorp: corp || undefined, commentPrefix: prefix,
    rows: rows.map((r) => ({ cardNo: r.cardNo, amount: r.amount, subsidy: r.subsidy || undefined })),
  });

  async function check() {
    setBusy('check'); setError(null); setDone(null);
    try {
      setPreview(await api.previewResultDailyCardUpload(payload()));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPreview(null);
    } finally { setBusy(null); }
  }

  async function commit() {
    setBusy('commit'); setError(null);
    try {
      const r = await api.commitResultDailyCardUpload(payload());
      setDone({ created: r.created, plus: r.plus, minus: r.minus });
      setRows([]); setPreview(null); setFileName('');
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  const errorCols: Column[] = [
    { key: 'row', label: '행', align: 'right' },
    { key: 'cardNo', label: '카드번호' },
    { key: 'errors', label: '오류', render: (r) => <span className="text-red-700">{(r.errors as string[]).join(' · ')}</span> },
  ];

  const matchCols: Column[] = [
    { key: 'cardNoRaw', label: '카드번호' },
    { key: 'matchedBy', label: '매칭', render: (r) => (
      r.matchedBy === 'last4'
        ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">뒤 4자리</span>
        : <span className="text-xs text-ink-faint">전체</span>
    ) },
    { key: 'grpName', label: '소속' },
    { key: 'ownerName', label: '차주' },
    { key: 'driverName', label: '기사' },
    { key: 'carNo', label: '차량번호' },
    { key: 'amount', label: '사용금액', align: 'right', render: (r) => money(r.amount as string) },
    ...(kind === '유류'
      ? [{ key: 'subsidy', label: '보조금', align: 'right' as const, render: (r: Row) => (r.subsidy ? money(r.subsidy as string) : '—') }]
      : []),
  ];

  const canCommit = preview !== null && preview.errorCount === 0 && !preview.isFinished && rows.length > 0;

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-sm font-bold">{driveMon} 카드 대량등록</h2>
        <span className="text-xs text-ink-faint">
          엑셀 열: {columns.join(' · ')} — <b>카드번호</b>로 편성을 찾습니다(빈 카드번호는 거부)
        </span>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          {KINDS.map((k) => (
            <button
              key={k}
              className={k === kind ? 'btn text-xs' : 'btn-ghost text-xs'}
              onClick={() => { setKind(k); setRows([]); setFileName(''); }}
              disabled={busy !== null}
            >
              {k}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1 text-xs text-ink-soft">
          카드사
          <select
            className="input py-1 text-xs"
            value={corp}
            onChange={(e) => { setCorp(e.target.value); setPreview(null); }}
            disabled={busy !== null}
          >
            <option value="">전체</option>
            {corps.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="flex items-center gap-1 text-xs text-ink-soft">
          비고 앞머리
          <input
            className="input w-56 py-1 text-xs"
            value={prefix}
            onChange={(e) => { setPrefix(e.target.value); setPreview(null); }}
            disabled={busy !== null}
          />
        </label>
      </div>

      <p className="text-xs text-ink-faint">
        비고는 <b>앞머리 + 계정별 꼬리</b>로 만들어집니다 — {kind === '유류'
          ? <>「{prefix} 유류사용분」 · 「{prefix} 유류사용 보조금」</>
          : <>「{prefix} 하이패스」</>}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn-ghost text-xs" onClick={downloadTemplate} disabled={busy !== null}>
          {busy === 'template' ? '만드는 중…' : '템플릿 내려받기'}
        </button>
        <label className="btn-ghost cursor-pointer text-xs">
          {busy === 'parse' ? '읽는 중…' : '엑셀 선택'}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={pick} disabled={busy !== null} />
        </label>
        {fileName && (
          <span className="text-sm">{fileName} — <b>{rows.length.toLocaleString('ko-KR')}행</b></span>
        )}
        {rows.length > 0 && (
          <button className="btn-ghost text-xs" onClick={check} disabled={busy !== null}>
            {busy === 'check' ? '검증 중…' : '검증'}
          </button>
        )}
      </div>

      {preview && (
        <>
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
            <span>총 <b>{preview.total.toLocaleString('ko-KR')}</b>행</span>
            <span className="text-emerald-700">매칭 {preview.okCount.toLocaleString('ko-KR')}</span>
            <span className={preview.errorCount > 0 ? 'font-bold text-red-700' : 'text-ink-faint'}>
              오류 {preview.errorCount.toLocaleString('ko-KR')}
            </span>
            <span className="text-ink-soft">
              생성될 항목 <b>{preview.willCreate.toLocaleString('ko-KR')}</b>
            </span>
            <span>사용금액 <b>{money(preview.sumUse)}</b></span>
            {kind === '유류' && <span>보조금 <b>{money(preview.sumSubsidy)}</b></span>}
            <span className="text-xs text-ink-faint">후보 편성 {preview.candidateCount.toLocaleString('ko-KR')}</span>
          </div>

          {preview.isFinished && (
            <ErrorBox message="관리자 정산이 완료된 달이라 대량 등록할 수 없습니다. 마감을 먼저 푸세요." />
          )}

          {preview.last4Count > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <b>{preview.last4Count.toLocaleString('ko-KR')}행</b>이 카드번호 <b>뒤 4자리</b>로 붙었습니다.
              전체 번호가 일치한 게 아니니 아래 표에서 기사·차량이 맞는지 확인하세요.
            </p>
          )}

          {preview.duplicateAllocs.length > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              같은 편성이 여러 번 나옵니다 — 편성번호 {preview.duplicateAllocs.map((d) => `${d.carAllocId}(${d.count}건)`).join(' · ')}.
              한 카드가 한 달에 두 줄로 청구되는 경우도 있으니 <b>막지는 않습니다.</b> 파일을 두 번 붙여넣지 않았는지만 보세요.
            </p>
          )}

          {preview.errorCount > 0 && (
            <>
              <p className="text-sm text-red-700">
                검증되지 않은 항목이 있습니다. <b>전부 고친 뒤</b> 다시 올려야 등록됩니다 —
                일부만 등록하면 어디까지 들어갔는지 알 수 없습니다.
              </p>
              <DataTable columns={errorCols} rows={preview.errors as unknown as Row[]} loading={false}
                         tableId="upload.card.errors" />
            </>
          )}

          {preview.okCount > 0 && (
            <DataTable columns={matchCols} rows={preview.rows as unknown as Row[]} loading={false}
                         tableId="upload.card.match" />
          )}

          {canCommit && (
            <button className="btn" onClick={commit} disabled={busy !== null}>
              {busy === 'commit' ? '등록 중…' : `${preview.willCreate.toLocaleString('ko-KR')}개 항목 등록 실행`}
            </button>
          )}
        </>
      )}

      {done && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <b>{done.created.toLocaleString('ko-KR')}개 항목을 등록했습니다.</b> 지급 {money(done.plus)} · 공제 {money(done.minus)}
        </div>
      )}
    </div>
  );
}
